import Image from "../../../elements/Image";
import Title from "../../../elements/Title";
import { classNames } from "../../../../helpers/classNames";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { postFrontDetails } from "../../../../services/postService";
import { toast } from "react-toastify";
import { lngDetect } from "../../../common/lngDetect";

const JourneySteps = () => {
  const { data: spinData } = useQuery({
    queryKey: ["front-spin-section"],
    queryFn: async () => {
      const res = await postFrontDetails({
        slug: "spin-section",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
  });

  const newSpinData = {
    title: lngDetect(spinData?.content)?.title,
    image: spinData?.mediaGallery?.[0]?.url,
    steps:
      (spinData?.sections || [])?.map((item) => ({
        icon: item?.icon,
        name: item?.heading,
        description: item?.content,
      })) || [],
  };

  return (
    <section className="relative py-10 xls:py-24 z-0" id="coins">
      <motion.div
        animate={{ opacity: [0.1, 0.2, 0.1], scale: [1, 1.2, 1] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="hidden lg:block absolute -top-[25vh] -left-[25vh] h-screen aspect-square bg-electric-violet/50 rounded-full blur-3xl z-[-1]"
      />
      <div className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xls:max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl mx-auto px-4">
        <div className="grid sm:grid-cols-7 gap-16 items-center">
          <motion.div
            className="sm:col-span-3 hidden sm:block"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="relative w-60 lg:w-72 xl:w-96  mx-auto flex overflow-hidden rounded-7xl"
            >
              <div className="absolute inset-0 blur-2xl bg-electric-violet/40 rounded-full"></div>
              <Image
                preload={true}
                src={newSpinData?.image}
                alt={`spin`}
                effect="blur"
                classes="object-contain relative z-10"
              />
            </motion.div>
          </motion.div>
          <motion.div
            className="sm:col-span-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            transition={{ staggerChildren: 0.2 }}
          >
            <div className="space-y-12 sm:space-y-8">
              <Title title={"Spin"} />
              {newSpinData.title && (
                <motion.h2
                  className="text-2xl sm:text-3xl xl:text-4xl font-bold leading-normal"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  transition={{ duration: 0.5 }}
                >
                  {newSpinData.title}
                </motion.h2>
              )}
              <motion.div
                className="block !my-24 sm:hidden"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                <motion.div
                  animate={{ y: [0, -15, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative w-60 lg:w-72 xl:w-96 mx-auto flex"
                >
                  <div className="absolute inset-0 blur-2xl bg-electric-violet/40 rounded-full"></div>
                  <Image
                    preload={true}
                    src={newSpinData?.image}
                    alt={`bg-image`}
                    effect="blur"
                    classes="object-contain relative z-10"
                  />
                </motion.div>
              </motion.div>
              {newSpinData.steps.length > 0 && (
                <div className="max-w-xl">
                  {newSpinData.steps.map((item, index) => (
                    <motion.div
                      key={index}
                      className={classNames(
                        "flex items-start gap-8 relative group transition-all z-0",
                        index < newSpinData.steps.length - 1 && "pb-10 lg:pb-16"
                      )}
                      variants={{
                        hidden: { opacity: 0, y: 30 },
                        visible: { opacity: 1, y: 0 },
                      }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                      {index < newSpinData.steps.length - 1 && (
                        <div className="absolute top-0 left-8 h-full border-l-4 border-yellow-400 group-hover:border-yellow-600 transition-all duration-300 shadow-[0_0_15px_rgba(250, 204, 21, 1)] z-[-1]" />
                      )}
                      <div className="flex-shrink-0 flex flex-col items-center justify-center w-16 aspect-square rounded-xl overflow-hidden bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-[0_0_20px_rgba(250, 204, 21, 0.7)] group-hover:shadow-[0_0_35px_rgba(250, 204, 21, 1)] transition-all duration-300">
                        <i className="fa fa-trophy text-3xl"></i>
                      </div>
                      <div className="flex-shrink flex-grow space-y-2">
                        <div className="text-lg xls:text-xl xl:text-2xl font-semibold">
                          {item?.title}
                        </div>
                        <div className="text-sm xl:text-base font-light text-white/90">
                          {item?.description}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default JourneySteps;
